<template>
  <v-dialog
    :value="visible"
    @input="$emit('update:visible', $event)"
    max-width="85%"
    scrollable
    content-class="ActionDocsModal"
  >
    <v-card class="ActionDocsModal__card">
      <v-card-title class="ActionDocsModal__title">
        <span class="text-h6">Documentation</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="ActionDocsModal__content">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-4">Loading documentation...</p>
        </div>

        <div v-else-if="error" class="text-center py-8">
          <v-icon color="warning" size="48">mdi-information-outline</v-icon>
          <p class="mt-4 text-h6">{{ error }}</p>
        </div>

        <div
          v-else-if="content"
          class="ActionDocsModal__markdown"
          v-html="renderedContent"
        ></div>

        <div v-else class="text-center py-8">
          <v-icon color="warning" size="48">mdi-information-outline</v-icon>
          <p class="mt-4 text-h6">Documentation is not available for this action type.</p>
          <p class="mt-2">Please reach out to the development team for more information.</p>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="closeModal">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import markdownit from 'markdown-it';
import Prism from 'prismjs';
// Import common language support
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-sql';
// Import Prism theme
import 'prismjs/themes/prism-tomorrow.css';

export default {
  name: 'ActionDocsModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    content: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  },
  computed: {
    renderedContent() {
      if (!this.content) {
        return '';
      }
      const md = markdownit({
        breaks: true,
        linkify: true,
        highlight: (str, lang) => {
          if (lang && Prism.languages[lang]) {
            try {
              return `<pre class="language-${lang}"><code class="language-${lang}">${Prism.highlight(str, Prism.languages[lang], lang)}</code></pre>`;
            } catch (e) {
              console.error('Prism highlighting error:', e);
            }
          }
          // Fallback for unknown languages
          return `<pre class="language-text"><code class="language-text">${md.utils.escapeHtml(str)}</code></pre>`;
        },
      });
      return md.render(this.content);
    },
  },
  methods: {
    closeModal() {
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style scoped lang="scss">
.ActionDocsModal {
  &__card {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }

  &__title {
    display: flex;
    align-items: center;
    padding: 16px 24px;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  &__markdown {
    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      margin-top: 24px;
      margin-bottom: 12px;
      font-weight: 600;
    }

    :deep(h1) {
      font-size: 2em;
      border-bottom: 2px solid #e0e0e0;
      padding-bottom: 8px;
    }

    :deep(h2) {
      font-size: 1.5em;
      border-bottom: 1px solid #e0e0e0;
      padding-bottom: 6px;
    }

    :deep(h3) {
      font-size: 1.25em;
    }

    :deep(p) {
      margin-bottom: 12px;
      line-height: 1.6;
    }

    :deep(ul),
    :deep(ol) {
      margin-bottom: 12px;
      padding-left: 24px;
    }

    :deep(li) {
      margin-bottom: 6px;
    }

    // Inline code styling (not in pre blocks)
    :deep(code:not([class*="language-"])) {
      background-color: rgba(175, 184, 193, 0.2);
      color: #24292f;
      padding: 0.2em 0.4em;
      border-radius: 6px;
      font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
      font-size: 85%;
    }

    // Prism code block enhancements
    :deep(pre[class*="language-"]) {
      border-radius: 8px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      font-size: 14px;
      line-height: 1.5;
    }

    :deep(code[class*="language-"]) {
      font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
      font-size: 14px;
      line-height: 1.5;
    }

    :deep(blockquote) {
      border-left: 4px solid #2196f3;
      padding-left: 16px;
      margin-left: 0;
      color: #666;
      font-style: italic;
    }

    :deep(table) {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;

      th,
      td {
        border: 1px solid #ddd;
        padding: 8px 12px;
        text-align: left;
      }

      th {
        background-color: #f5f5f5;
        font-weight: 600;
      }
    }

    :deep(a) {
      color: #2196f3;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    :deep(strong) {
      font-weight: 600;
    }

    :deep(em) {
      font-style: italic;
    }
  }
}
</style>

